import { api } from "@/lib/api";
import type { AuditLogEntry } from "@/lib/schema";

export interface AuditLogPage {
  entries: AuditLogEntry[];
  total: number;
}

export interface AuditLogFilters {
  limit?: number;
  offset?: number;
  entityType?: string;
  actorId?: string;
  actionType?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  lang?: string;
}

/** Server caps `limit` at 200 per request. */
const EXPORT_BATCH = 200;

export const AuditLogService = {
  async findAll(filters?: AuditLogFilters): Promise<AuditLogPage> {
    const params = new URLSearchParams();
    if (filters?.limit) params.set("limit", filters.limit.toString());
    if (filters?.offset) params.set("offset", filters.offset.toString());
    if (filters?.entityType) params.set("entityType", filters.entityType);
    if (filters?.actorId) params.set("actorId", filters.actorId);
    if (filters?.actionType) params.set("actionType", filters.actionType);
    if (filters?.search) params.set("search", filters.search);
    if (filters?.startDate) params.set("startDate", filters.startDate);
    if (filters?.endDate) params.set("endDate", filters.endDate);
    if (filters?.lang) params.set("lang", filters.lang);
    return api.get<AuditLogPage>(`/api/audit-logs?${params.toString()}`);
  },

  /**
   * Every entry matching the filters, fetched in server-capped batches (for
   * export). Throws if any batch fails so a partial trail is never exported.
   */
  async findAllMatching(
    filters: Omit<AuditLogFilters, "limit" | "offset">,
  ): Promise<AuditLogEntry[]> {
    const all: AuditLogEntry[] = [];
    for (;;) {
      const page = await AuditLogService.findAll({
        ...filters,
        limit: EXPORT_BATCH,
        offset: all.length,
      });
      all.push(...page.entries);
      if (page.entries.length < EXPORT_BATCH || all.length >= page.total) {
        return all;
      }
    }
  },
};
